/**
 * latest-cards-dark block (14 uses)
 *
 * Dark background section with heading + 3-column card grid.
 * Cards have image, optional label badge, heading, and copy.
 * Each card is fully clickable (absolute-positioned link).
 * 2026 reskin: #161415 bg, bordered badges, updated typography.
 *
 * Hugo template: blocks/latest-cards-dark.html
 */

interface LatestCard {
  image?: string;
  label?: string;
  heading: string;
  copy?: string;
  url: string;
}

function shouldOpenCardInNewTab(card: LatestCard, index: number) {
  return index === 0 && card.label?.trim().toLowerCase() === "brief";
}

export function LatestCardsDark({
  heading,
  cards,
  block,
  background,
}: {
  heading?: string;
  cards?: LatestCard[];
  block?: string;
  background?: string;
}) {
  // latest-cards-light => bg-white with dark text
  // latest-card-grid   => bg-gray-50 or bg-white based on background param
  // latest-cards-dark  => bg-[#141414] (default)
  const isLight = block === "latest-cards-light";
  const isGrid = block === "latest-card-grid";
  const bgClass = isLight
    ? "bg-white"
    : isGrid
      ? background === "gray"
        ? "bg-gray-50"
        : "bg-white"
      : "bg-[#141414]";
  const headingClass = isLight || isGrid
    ? "font-display text-19 font-bold tracking-[-0.5%] text-charcoal md:text-23 md:font-bold"
    : "font-display text-19 font-bold tracking-[-0.5%] text-white md:text-23 md:font-bold";
  const cardHeadingClass = isLight || isGrid ? "text-charcoal" : "text-gray-30";
  const labelClass = isLight || isGrid
    ? "bg-gray-10 text-gray-60"
    : "border border-gray-60 text-gray-40";

  return (
    <div className={`${bgClass} px-4 py-16 md:px-8 md:py-24`}>
      <div className="mx-auto max-w-screen-xl">
        {heading && (
          <h2 className={headingClass}>
            {heading}
          </h2>
        )}
        {cards && cards.length > 0 && (
          <div className="mt-6 grid gap-9 lg:grid-cols-3 lg:gap-7">
            {cards.map((card, i) => {
              const opensInNewTab = shouldOpenCardInNewTab(card, i);

              return (
                <div key={i} className="group relative">
                  {card.image && (
                    <div className="aspect-[16/8.5] overflow-hidden rounded-10">
                      <img
                        src={card.image}
                        alt={card.heading}
                        className="h-full w-full object-cover object-center transition duration-300 ease-in group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  )}
                  {card.label && (
                    <p className={`mt-5 inline-block rounded px-2 py-1 font-body text-xxs uppercase leading-tighter tracking-widest-4 transition duration-300 ease-in group-hover:text-gray-40 ${labelClass}`}>
                      {card.label}
                    </p>
                  )}
                  <h3 className={`mt-3 font-body text-base font-bold md:text-19 md:font-bold ${cardHeadingClass}`}>
                    {card.heading}
                  </h3>
                  {card.copy && <p className="mt-2 font-body text-gray-60">{card.copy}</p>}
                  <a
                    href={card.url}
                    className="absolute inset-0"
                    title={card.heading}
                    target={opensInNewTab ? "_blank" : undefined}
                    rel={opensInNewTab ? "noopener noreferrer" : undefined}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
